import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@finance/components/ui/sheet";
import { toast } from "sonner";

const near = (a, b) => Math.abs((a || 0) - (b || 0)) < 0.01;

export function TieMark({ ok, testid }) {
  return <span data-testid={testid} className={`ml-2 rounded-sm px-1.5 py-0.5 text-[11px] font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{ok ? "✓ ties" : "⚠ does not tie"}</span>;
}

export function AccountDrill({ target, onClose }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!target) { setD(null); return; }
    const { code, col, bs } = target;
    api.get("/fin-reports-drill/account", { params: { code, start: col.start, end: col.end, bs } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [target]);
  const open = async (r) => {
    try { const { data } = await api.get("/gl/source-link", { params: { source_type: r.source_type, source_id: r.source_id } }); if (data.route) navigate(data.route); else toast.info("No source screen for this entry"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const figure = d ? (target.bs ? d.closing : d.movement) * (target.sign || 1) : 0;
  return (
    <Sheet open={!!target} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[96vw] overflow-y-auto sm:max-w-[1100px]" data-testid="fin-drill-sheet">
        <SheetHeader><SheetTitle className="font-display">{target?.code} · {d?.name || ""} <span className="text-sm font-normal text-gray-500">{target?.col.label}</span></SheetTitle></SheetHeader>
        {!d ? <div className="p-4 text-sm text-gray-400">Loading…</div> : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 lx-label"><tr><th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Memo</th><th className="px-2 py-2 text-left">Counterparty</th><th className="px-2 py-2 text-right">Debit</th><th className="px-2 py-2 text-right">Credit</th><th className="px-2 py-2 text-right">Balance</th></tr></thead>
              <tbody>
                {target.bs && <tr className="border-b bg-gray-50 font-medium"><td className="px-2 py-1.5" colSpan={5}>Opening balance</td><td className="px-2 py-1.5 text-right lx-num" data-testid="drill-opening">{fmtNum(d.opening)}</td></tr>}
                {d.rows.map((r, i) => (
                  <tr key={i} className="cursor-pointer border-b border-gray-100 hover:bg-blue-50" data-testid={`drill-row-${i}`} onClick={() => open(r)}>
                    <td className="px-2 py-1.5">{fmtDate(r.date)}</td><td className="px-2 py-1.5">{r.memo}</td><td className="px-2 py-1.5">{r.counterparty || "—"}</td>
                    <td className="px-2 py-1.5 text-right lx-num">{r.debit ? fmtNum(r.debit) : ""}</td><td className="px-2 py-1.5 text-right lx-num">{r.credit ? fmtNum(r.credit) : ""}</td>
                    <td className="px-2 py-1.5 text-right lx-num">{fmtNum(r.balance)}</td>
                  </tr>
                ))}
                {!d.rows.length && <tr><td colSpan={6} className="px-2 py-4 text-center text-gray-400">No transactions in this period.</td></tr>}
                <tr className="bg-gray-50 font-semibold"><td className="px-2 py-2" colSpan={3}>{target.bs ? "Closing balance" : "Movement for the period"}</td><td className="px-2 py-2 text-right lx-num">{fmtNum(d.total_debit)}</td><td className="px-2 py-2 text-right lx-num">{fmtNum(d.total_credit)}</td>
                  <td className="px-2 py-2 text-right lx-num" data-testid="drill-total">{fmtNum(figure)}<TieMark ok={near(figure, target.value)} testid="drill-tie" /></td></tr>
              </tbody>
            </table>
            <div className="mt-2 text-xs text-gray-500">Clicked figure: {fmtNum(target.value)} · Click any row to open its source record.</div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
